import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { Tables } from "~/api/tables";
import { resolveRatePresetCents } from "~/utils/billing-rate";
import { db } from "~/api/db";

export type Client = Tables<"clients">;
export type Invoice = Tables<"invoices">;
export type InvoiceAmendment = Tables<"invoice_amendments">;
export type InvoiceItem = Tables<"invoice_items">;
export type InvoicePayment = Tables<"invoice_payments">;
export type InvoiceReminder = Tables<"invoice_reminders">;
export type InvoiceRefund = Tables<"invoice_refunds">;
export type Project = Tables<"projects">;
export type ProjectStatus = Tables<"project_statuses">;
export type RecurringInvoice = Tables<"recurring_invoices">;
export type RecurringTaskRule = Tables<"recurring_task_rules">;
export type ReportView = Tables<"report_views">;
export type TaxSetAsideEntry = Tables<"tax_set_aside_entries">;
export type Task = Tables<"tasks">;
export type TimeEntryTag = Tables<"time_entry_tags">;
export type TimeEntry = Tables<"time_entries"> & {
  tags: Pick<TimeEntryTag, "id" | "name">[];
};
export type UserRate = Tables<"user_rates">;
export type Workspace = Tables<"workspaces">;
export type WorkspaceInvitation = Tables<"workspace_invitations">;
/** The owner is also listed as a member by role, so "owner" is a possible role here. */
export type WorkspaceMember = Omit<Tables<"workspace_members">, "role"> & {
  role: "owner" | "admin" | "member" | "viewer";
};
export type Quote = Tables<"quotes">;
export type QuoteItem = Tables<"quote_items">;
export type MileageEntry = Tables<"mileage_entries">;
export type FixedAsset = Tables<"fixed_assets">;
export type AssetDepreciationEntry = Tables<"asset_depreciation_entries">;
export type QuoteWithItems = Quote & { quote_items: QuoteItem[] };
export type WorkspaceRole = "admin" | "member" | "viewer";

export const rootKey = ["mintstint-workspace"] as const;

/** Every per-workspace list query. A name that is not listed here fails to
 * typecheck, so a query and its invalidations cannot drift apart. */
export type WorkspaceQueryName =
  | "assets"
  | "clients"
  | "expenses"
  | "invoice-lifecycle"
  | "invoices"
  | "mileage"
  | "project-statuses"
  | "projects"
  | "quotes"
  | "rebillable-expenses"
  | "recurring-invoices"
  | "recurring-tasks"
  | "report-views"
  | "tasks"
  | "tax-set-aside"
  | "team"
  | "time-entries"
  | "time-entry-tags"
  | "user-rates";

export const workspaceKey = (
  name: WorkspaceQueryName,
  workspaceId: string | undefined,
) => [...rootKey, name, workspaceId ?? ""] as const;

export function textOrNull(value: string | undefined) {
  const trimmed = value?.trim() ?? "";
  return trimmed ? trimmed : null;
}

export function requireText(value: string | undefined, label: string) {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) throw new Error(`${label} is required.`);
  return trimmed;
}

export function parseRecurringLineItems(raw: string | undefined) {
  const value = raw?.trim() ?? "";
  if (!value) throw new Error("Add at least one recurring line item.");

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Recurring line items could not be read.");
  }

  if (!Array.isArray(parsed)) {
    throw new Error("Recurring line items are required.");
  }

  const items = parsed
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const description =
        typeof record.description === "string" ? record.description.trim() : "";
      const quantity = Number(record.quantity);
      const unitAmount = Number(record.unitAmount);
      const gstRate = Number(record.gstRate);

      if (!description) return null;
      if (!Number.isFinite(gstRate) || gstRate < 0 || gstRate > 100) {
        throw new Error("GST rate must be between 0 and 100.");
      }
      if (!Number.isFinite(quantity) || quantity <= 0) {
        throw new Error("Quantity must be greater than zero.");
      }
      if (!Number.isFinite(unitAmount) || unitAmount <= 0) {
        throw new Error("Unit amount must be greater than zero.");
      }

      return {
        description,
        gst_rate: gstRate,
        quantity,
        unit_amount_cents: Math.round(unitAmount * 100),
      };
    })
    .filter(
      (
        item,
      ): item is {
        description: string;
        gst_rate: number;
        quantity: number;
        unit_amount_cents: number;
      } => Boolean(item),
    );

  if (items.length === 0) {
    throw new Error("Add at least one recurring line item.");
  }

  return items;
}

export function projectBudgetType(
  value: string | undefined,
): Project["budget_type"] {
  if (value === "fee_cap" || value === "hours_cap") return value;
  return "fixed_fee";
}

export function taskPriority(value: string | undefined): Task["priority"] {
  if (value === "low" || value === "high" || value === "urgent") return value;
  return "medium";
}

export function invoiceRoundingMethod(
  value: string | undefined,
): "nearest_0_05" | "none" {
  return value === "nearest_0_05" ? value : "none";
}

export function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

export function parseTagNames(value: string | undefined) {
  return (value ?? "")
    .split(",")
    .map((tag) => tag.trim().toLowerCase())
    .filter((tag) => tag.length > 0 && tag.length <= 40);
}

export function documentReferenceFields(input: {
  purchaseOrderReference?: string;
  clientReference?: string;
  jobStartsOn?: string;
  jobEndsOn?: string;
}) {
  const jobStartsOn = textOrNull(input.jobStartsOn);
  const jobEndsOn = textOrNull(input.jobEndsOn);
  if (jobStartsOn && !/^\d{4}-\d{2}-\d{2}$/.test(jobStartsOn)) {
    throw new Error("Job start date must be YYYY-MM-DD.");
  }
  if (jobEndsOn && !/^\d{4}-\d{2}-\d{2}$/.test(jobEndsOn)) {
    throw new Error("Job end date must be YYYY-MM-DD.");
  }
  if (jobStartsOn && jobEndsOn && jobEndsOn < jobStartsOn) {
    throw new Error("Job end date must be on or after the start date.");
  }
  return {
    purchase_order_reference: textOrNull(input.purchaseOrderReference),
    client_reference: textOrNull(input.clientReference),
    job_starts_on: jobStartsOn,
    job_ends_on: jobEndsOn,
  };
}

export async function resolveTimeEntryLinks(
  workspaceId: string,
  input: { clientId?: string; projectId?: string; taskId?: string },
) {
  const taskId = textOrNull(input.taskId);
  let projectId = textOrNull(input.projectId);
  let clientId = textOrNull(input.clientId);

  if (taskId) {
    const { data: task, error } = await db
      .from("tasks")
      .select("id, project_id")
      .eq("workspace_id", workspaceId)
      .eq("id", taskId)
      .maybeSingle();

    if (error) throw error;
    if (!task) throw new Error("Selected task was not found.");
    projectId = task.project_id ?? projectId;
  }

  if (projectId) {
    const { data: project, error } = await db
      .from("projects")
      .select("id, client_id")
      .eq("workspace_id", workspaceId)
      .eq("id", projectId)
      .maybeSingle();

    if (error) throw error;
    if (!project) throw new Error("Selected project was not found.");
    clientId = project.client_id ?? clientId;
  }

  return { clientId, projectId, taskId };
}

export async function resolveTimeEntryRateCents(input: {
  clientId: string | null;
  date: string;
  projectId: string | null;
  userId: string;
  workspaceId: string;
}) {
  const { data: rates, error: ratesError } = await db
    .from("user_rates")
    .select("client_id, effective_from, hourly_rate_cents, project_id")
    .eq("workspace_id", input.workspaceId)
    .eq("user_id", input.userId)
    .lte("effective_from", input.date);
  if (ratesError) throw ratesError;

  const presetRate = resolveRatePresetCents(rates ?? [], {
    clientId: input.clientId,
    date: input.date,
    projectId: input.projectId,
  });
  if (presetRate !== null) return presetRate;

  if (input.projectId) {
    const { data: project, error: projectError } = await db
      .from("projects")
      .select("hourly_rate_cents")
      .eq("workspace_id", input.workspaceId)
      .eq("id", input.projectId)
      .maybeSingle();
    if (projectError) throw projectError;
    if (project?.hourly_rate_cents) return project.hourly_rate_cents;
  }

  const { data: workspace, error: workspaceError } = await db
    .from("workspaces")
    .select("default_hourly_rate_cents")
    .eq("id", input.workspaceId)
    .single();
  if (workspaceError) throw workspaceError;
  return workspace.default_hourly_rate_cents || null;
}

export function useWorkspaceMutation<TInput, TResult = unknown>(
  workspaceId: string | undefined,
  tableKey: string,
  mutationFn: (input: TInput, workspaceId: string) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      return mutationFn(input, workspaceId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...rootKey, tableKey, workspaceId],
      });
    },
  });
}
